import { useEffect, useState } from 'react';
import type { FactoryMetrics } from '@reef/schema';
import { api, type FactoryLoop } from '../api.ts';

/**
 * The factory as a whole: three loops and three numbers.
 *
 * The numbers are together on purpose. Throughput on its own flatters a factory
 * that is falling behind; backlog on its own cannot tell a queue that is
 * growing from one that is merely long; cycle time on its own says nothing
 * about volume. Any two of them mislead, so the tile row shows all three and
 * the failure rate that qualifies them.
 *
 * The loops are drawn from what the control plane says is deployed, not from a
 * picture. A stage this deployment has not got renders as an outline rather
 * than being quietly omitted — "the drift loop is not running here" is the fact
 * somebody needs, and a diagram that drew four boxes out of five would hide it.
 */
export function FactoryOverviewView() {
  const [loops, setLoops] = useState<FactoryLoop[]>([]);
  const [metrics, setMetrics] = useState<FactoryMetrics | null>(null);
  const [windowHours, setWindowHours] = useState(24);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .factoryLoops()
      .then((result) => setLoops(result.loops))
      .catch((caught) => setError((caught as Error).message));
  }, []);

  useEffect(() => {
    let current = true;
    api
      .factoryMetrics(windowHours)
      .then((result) => current && setMetrics(result.metrics))
      .catch((caught) => current && setError((caught as Error).message));
    return () => {
      current = false;
    };
  }, [windowHours]);

  return (
    <div className="studio">
      <header className="studio-head">
        <span className="studio-crumb">
          <span className="studio-crumb-root">Software factory</span>
        </span>
        <span className="spacer" />
        <label className="studio-window">
          Window
          <select
            value={windowHours}
            onChange={(event) => setWindowHours(Number(event.target.value))}
            aria-label="Metrics window"
          >
            <option value={24}>24 hours</option>
            <option value={24 * 7}>7 days</option>
            <option value={24 * 30}>30 days</option>
          </select>
        </label>
      </header>

      <div className="studio-column wide">
        {error && <div className="banner danger">{error}</div>}

        <section className="studio-card">
          <div className="studio-card-head">
            <h2>Factory health</h2>
            {metrics && (
              <span className="chip">
                over {metrics.windowHours >= 48 ? `${metrics.windowHours / 24} days` : `${metrics.windowHours} hours`}
              </span>
            )}
          </div>
          <div className="studio-card-body">
            <div className="tiles">
              <Tile
                label="Throughput"
                value={metrics ? String(metrics.throughput) : '—'}
                note="runs completed"
              />
              <Tile
                label="Backlog"
                value={metrics ? String(metrics.backlog) : '—'}
                note="runs still in flight"
              />
              <Tile
                label="Cycle time"
                value={
                  metrics?.cycleTimeHours === null || metrics === null
                    ? '—'
                    : `${metrics.cycleTimeHours.toFixed(1)}h`
                }
                note="median, start to finish"
              />
              <Tile
                label="Failure rate"
                value={metrics ? `${Math.round(metrics.failureRate * 100)}%` : '—'}
                note="of runs that ended"
                tone={metrics && metrics.failureRate > 0.2 ? 'warn' : undefined}
              />
              <Tile
                label="Escalations"
                value={metrics ? String(metrics.escalations) : '—'}
                note="runs waiting on a person"
                tone={metrics && metrics.escalations > 0 ? 'warn' : undefined}
              />
            </div>

            <p className="hint">
              Computed from the run history rather than from a counter, so every number here can be
              traced back to the runs behind it.
            </p>
          </div>
        </section>

        {loops.map((loop) => (
          <section className="studio-card" key={loop.id}>
            <div className="studio-card-head">
              <h2>{loop.name}</h2>
              <span className="chip">{loop.stages.filter((stage) => stage.present).length} of {loop.stages.length} deployed</span>
            </div>
            <div className="studio-card-body">
              <p className="hint" style={{ marginTop: 0 }}>
                {loop.description}
              </p>
              <ol className="loop">
                {loop.stages.map((stage, index) => (
                  <li key={`${stage.id}-${index}`} className={stage.present ? '' : 'absent'}>
                    <span className="loop-stage">{stage.id.replace(/^factory-/, '')}</span>
                    {index < loop.stages.length - 1 && (
                      <span className="loop-arrow" aria-hidden="true">
                        →
                      </span>
                    )}
                  </li>
                ))}
                {/* The loops close: the last stage feeds the first. */}
                <li className="loop-back" aria-hidden="true">
                  ↺
                </li>
              </ol>
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

function Tile({
  label,
  value,
  note,
  tone,
}: {
  label: string;
  value: string;
  note: string;
  tone?: 'warn';
}) {
  return (
    <div className={`tile ${tone ?? ''}`}>
      <p className="tile-label">{label}</p>
      <p className="tile-value">{value}</p>
      <p className="tile-note">{note}</p>
    </div>
  );
}
